import cloneDeep from 'lodash/cloneDeep';
import { type MouseEvent, useCallback, useState } from 'react';
import { useNormalizedDefinitionValidation } from '../../../../../hooks/useNormalizedDefinitionValidation.ts';
import type { AudienceListType, FormSegment, Group, GroupFieldName } from '../../../../../types.ts';
import type { DrawerState } from '../../../drawerTypes.ts';
import { drawerType } from '../../../drawerTypes.ts';
import { isIndexedGroupFieldName } from '../../../utils/getGroupFromPath.ts';

export type UseSegmentsSectionHandlersProps = {
  fieldAsGroup: Group;
  groupName: string;
  groupFieldName: GroupFieldName;
  listType: AudienceListType;
  index: number;
  update: (index: number, value: Group) => void;
  setDrawerState: (state: DrawerState) => void;
  onShrinkSection: () => void;
  onExpandSection: () => void;
};

export const useSegmentsSectionHandlers = ({
  fieldAsGroup,
  groupName,
  groupFieldName,
  listType,
  index,
  update,
  setDrawerState,
  onShrinkSection,
  onExpandSection,
}: UseSegmentsSectionHandlersProps) => {
  const [showClearModal, setShowClearModal] = useState(false);
  const { withValidation } = useNormalizedDefinitionValidation();

  const updateWithValidation = useCallback(withValidation(update), []);

  const handleEditClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (!isIndexedGroupFieldName(groupFieldName)) {
        return;
      }

      const newDrawerState: DrawerState = {
        isOpen: true,
        type: drawerType.SEGMENTS,
        title: groupName,
        marketContext: {
          region: fieldAsGroup.region ?? null,
          countries: fieldAsGroup.countries ?? [],
        },
        fieldPath: groupFieldName,
        updater: value => updateWithValidation(index, value),
        listType,
        onShrinkSection,
        onExpandSection,
      };
      setDrawerState(newDrawerState);
    },
    [
      setDrawerState,
      groupName,
      groupFieldName,
      updateWithValidation,
      index,
      listType,
      onShrinkSection,
      onExpandSection,
      fieldAsGroup.region,
      fieldAsGroup.countries,
    ]
  );

  const handleRemoveSegment = useCallback(
    (segment: FormSegment) => {
      const updatedGroup = cloneDeep(fieldAsGroup);

      if (updatedGroup.direct?.segments) {
        updatedGroup.direct.segments = updatedGroup.direct.segments.filter(
          directSegment => directSegment.id !== segment.id
        );
      }

      if (updatedGroup.predefined?.segments) {
        updatedGroup.predefined.segments = updatedGroup.predefined.segments.filter(
          predefinedSegment => predefinedSegment.id !== segment.id
        );
      }

      const remainingSegmentCount =
        (updatedGroup.direct?.segments?.length ?? 0) + (updatedGroup.predefined?.segments?.length ?? 0);

      updateWithValidation(index, updatedGroup);

      if (remainingSegmentCount === 0) {
        onShrinkSection();
      }
    },
    [fieldAsGroup, updateWithValidation, index, onShrinkSection]
  );

  const handleClearAll = useCallback(() => {
    const updatedGroup = cloneDeep(fieldAsGroup);

    if (updatedGroup.direct) {
      updatedGroup.direct.segments = [];
    }

    if (updatedGroup.predefined) {
      updatedGroup.predefined.segments = [];
    }

    updateWithValidation(index, updatedGroup);
    onShrinkSection();
  }, [fieldAsGroup, updateWithValidation, index, onShrinkSection]);

  const handleClearButtonClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setShowClearModal(true);
  }, []);

  const handleClearConfirm = useCallback(() => {
    handleClearAll();
    setShowClearModal(false);
  }, [handleClearAll]);

  const handleClearCancel = useCallback(() => {
    setShowClearModal(false);
  }, []);

  return {
    showClearModal,
    handleEditClick,
    handleRemoveSegment,
    handleClearButtonClick,
    handleClearConfirm,
    handleClearCancel,
  };
};
